export type OverflowCollapseFrom = 'end' | 'start'

export interface OverflowMeasuredItem {
  readonly inlineSize: number
}

export interface OverflowPartitionItem<T> {
  readonly item: T
  readonly index: number
}

export interface OverflowPartitionOptions {
  readonly availableInlineSize: number
  readonly collapseFrom: OverflowCollapseFrom
  readonly gap: number
  readonly minVisibleItems: number
  readonly overflowInlineSize?: number
}

export interface OverflowPartition<T> {
  readonly visibleItems: readonly OverflowPartitionItem<T>[]
  readonly overflowItems: readonly OverflowPartitionItem<T>[]
}

function dimension(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0
}

/**
 * Partitions measured items into contiguous visible and overflowed sets.
 * Results always retain input order and expose each item's original index.
 */
export function partitionOverflowItems<T extends OverflowMeasuredItem>(
  items: readonly T[],
  options: OverflowPartitionOptions,
): OverflowPartition<T> {
  const indexedItems = items.map((item, index) => ({ item, index }))
  const sizes = items.map(item => dimension(item.inlineSize))
  const availableInlineSize = dimension(options.availableInlineSize)
  const hasOverflowAffordance = options.overflowInlineSize !== undefined
  const overflowInlineSize = dimension(options.overflowInlineSize ?? 0)
  const gap = dimension(options.gap)
  const minVisibleItems = Math.min(
    items.length,
    Math.max(0, Math.floor(dimension(options.minVisibleItems))),
  )

  const prefixSums = [0]
  for (const size of sizes) {
    prefixSums.push(prefixSums[prefixSums.length - 1] + size)
  }

  const sizeBetween = (start: number, end: number) => prefixSums[end] - prefixSums[start]
  const allItemsInlineSize = sizeBetween(0, items.length) + gap * Math.max(0, items.length - 1)

  if (allItemsInlineSize <= availableInlineSize) {
    return { visibleItems: indexedItems, overflowItems: [] }
  }

  let visibleCount = minVisibleItems
  for (let count = items.length - 1; count >= minVisibleItems; count--) {
    const start = options.collapseFrom === 'start' ? items.length - count : 0
    const visibleInlineSize = sizeBetween(start, start + count)
    const gapsInlineSize = count > 0
      ? gap * (count - 1 + Number(hasOverflowAffordance))
      : 0

    if (visibleInlineSize + overflowInlineSize + gapsInlineSize <= availableInlineSize) {
      visibleCount = count
      break
    }
  }

  const splitIndex = options.collapseFrom === 'start'
    ? items.length - visibleCount
    : visibleCount

  if (options.collapseFrom === 'start') {
    return {
      visibleItems: indexedItems.slice(splitIndex),
      overflowItems: indexedItems.slice(0, splitIndex),
    }
  }

  return {
    visibleItems: indexedItems.slice(0, splitIndex),
    overflowItems: indexedItems.slice(splitIndex),
  }
}
